import { getSupabaseClient } from './supabase';
import { Website, WebsiteStats } from '../types';
import { sortWebsitesByOrder } from '../utils/countryFlags';

export const websiteService = {
  async getWebsites(): Promise<{ websites: Website[]; error: string | null }> {
    try {
      console.log('🔍 Fetching websites from Supabase...');
      const { data, error } = await getSupabaseClient(true)
        .from('websites')
        .select('*')
        .neq('country', 'GLOBAL')
        .order('website_id');

      if (error) {
        console.error('❌ Error fetching websites:', error);
        return { websites: [], error: error.message };
      }

      console.log('✅ Fetched websites:', data);

      const websites: Website[] = (data || []).map((item) => ({
        id: item.id,
        website_id: item.website_id,
        base_url: item.base_url,
        country: item.country,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));

      // Sort websites according to the predefined order
      const sortedWebsites = sortWebsitesByOrder(websites);

      return { websites: sortedWebsites, error: null };
    } catch (error: any) {
      return { websites: [], error: error.message };
    }
  },

  async getWebsiteById(id: number): Promise<{ website: Website | null; error: string | null }> {
    try {
      const { data, error } = await getSupabaseClient(true)
        .from('websites')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        return { website: null, error: error.message };
      }

      const website: Website = {
        id: data.id,
        website_id: data.website_id,
        base_url: data.base_url,
        country: data.country,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return { website, error: null };
    } catch (error: any) {
      return { website: null, error: error.message };
    }
  },

  async createWebsite(
    websiteData: Omit<Website, 'id' | 'created_at' | 'updated_at'>
  ): Promise<{ website: Website | null; error: string | null }> {
    try {
      const { data, error } = await getSupabaseClient(true)
        .from('websites')
        .insert([{
          website_id: websiteData.website_id,
          base_url: websiteData.base_url,
          country: websiteData.country
        }])
        .select()
        .single();

      if (error) {
        return { website: null, error: error.message };
      }

      const website: Website = {
        id: data.id,
        website_id: data.website_id,
        base_url: data.base_url,
        country: data.country,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return { website, error: null };
    } catch (error: any) {
      return { website: null, error: error.message };
    }
  },

  async updateWebsite(
    id: number,
    updateData: Partial<Website>
  ): Promise<{ website: Website | null; error: string | null }> {
    try {
      const { website_id, base_url, country } = updateData;
      
      const { data, error } = await getSupabaseClient(true)
        .from('websites')
        .update({
          ...(website_id && { website_id }),
          ...(base_url && { base_url }),
          ...(country && { country }),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { website: null, error: error.message };
      }

      const website: Website = {
        id: data.id,
        website_id: data.website_id,
        base_url: data.base_url,
        country: data.country,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return { website, error: null };
    } catch (error: any) {
      return { website: null, error: error.message };
    }
  },

  async deleteWebsite(id: number): Promise<{ error: string | null }> {
    try {
      const { error } = await getSupabaseClient(true).from('websites').delete().eq('id', id);

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async getWebsiteStats(websiteId: string): Promise<{ stats: WebsiteStats | null; error: string | null }> {
    try {
      // Get keyword count
      const { data: keywordsData, error: keywordsError } = await getSupabaseClient(true)
        .from('keywords')
        .select('id, keyword')
        .eq('url_id', websiteId);
        
      if (keywordsError) {
        return { stats: null, error: keywordsError.message };
      }

      // Get URL count
      const { data: urlsData, error: urlsError } = await getSupabaseClient(true)
        .from('urls')
        .select('url_id')
        .eq('website_id', websiteId);
        
      if (urlsError) {
        return { stats: null, error: urlsError.message };
      }
      
      const urlIds = (urlsData || []).map(u => u.url_id);

      // Get the latest rankings for position stats
      const { data: rankingsData, error: rankingsError } = await getSupabaseClient(true)
        .from('serp_rankings')
        .select('ranking, keyword')
        .in('url_id', urlIds)
        .order('check_date', { ascending: false });

      if (rankingsError) {
        return { stats: null, error: rankingsError.message };
      }

      // Calculate stats
      const totalKeywords = keywordsData?.length || 0;
      const totalUrls = urlsData?.length || 0;
      
      // Get latest position for each keyword
      const latestRankingsByKeyword = new Map<string, number>();
      (rankingsData || []).forEach((ranking: any) => {
        if (!latestRankingsByKeyword.has(ranking.keyword)) {
          latestRankingsByKeyword.set(ranking.keyword, ranking.ranking);
        }
      });
      
      const positions = Array.from(latestRankingsByKeyword.values());
      const avgPosition = positions.length > 0 
        ? positions.reduce((sum, pos) => sum + pos, 0) / positions.length 
        : 0;
      
      const topKeywords = positions.filter(pos => pos <= 10).length;

      const stats: WebsiteStats = {
        totalKeywords,
        totalUrls,
        avgPosition: Math.round(avgPosition * 10) / 10,
        topKeywords
      };

      return { stats, error: null };
    } catch (error: any) {
      return { stats: null, error: error.message };
    }
  }
};

export default websiteService;